<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.dashboard'),
                        href: meta.links.dashboard_index,
                    },
                    {
                        name: $tc('messages.qualitative_reports', 2),
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="w-full flex space-x-4 justify-end">
            <AppButtonOptionsPanel :button-text="$t('messages.preview')">
                <a
                    :href="gradeLevel.url"
                    v-for="gradeLevel in meta.links.grade_level_previews"
                    :key="gradeLevel.id"
                    class="p-1 hover:bg-gray-100 w-full text-indigo-500 block"
                >
                    {{ gradeLevel.text }}
                </a>
            </AppButtonOptionsPanel>

            <AppButton :href="meta.links.create" element="inertia-link">
                {{ $t('messages.create') }}
            </AppButton>
        </div>

        <EmptyState picture v-if="qualitativeReportCategories.data.length === 0" />

        <div class="mt-5">
            <div
                v-for="qualitativeReportCategory in qualitativeReportCategories.data"
                :key="qualitativeReportCategory.id"
            >
                <div class="flex justify-between">
                    <span class="font-semibold">
                        {{ qualitativeReportCategory.category_order }} -
                        {{ qualitativeReportCategory.title }}
                    </span>
                    <div class="flex space-x-1 justify-end">
                        <InertiaSquareLinkButton action="edit" :href="qualitativeReportCategory.links.edit" />
                        <InertiaSquareConfirmActionButton
                            :confirm-text="$t('messages.delete')"
                            :href="qualitativeReportCategory.links.delete"
                            danger
                            method="delete"
                        >
                            <template v-slot:icon>
                                <svg viewBox="0 0 20 20" fill="currentColor" class="trash w-4 h-4">
                                    <path
                                        fill-rule="evenodd"
                                        d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                        clip-rule="evenodd"
                                    ></path>
                                </svg>
                            </template>
                        </InertiaSquareConfirmActionButton>
                    </div>
                </div>
                <div class="bg-gray-100 overflow-hidden rounded-lg mt-3">
                    <div class="px-4 py-5 sm:p-6">
                        <div class="flex flex-col mt-5" v-if="qualitativeReportCategory.subcategories.length > 0">
                            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                                <div
                                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                                >
                                    <table class="min-w-full divide-y divide-gray-200">
                                        <template
                                            v-for="subcategory in qualitativeReportCategory.subcategories"
                                            :key="`${subcategory.id}_${subcategory.updated_at}`"
                                        >
                                            <thead>
                                                <tr>
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 tracking-wider"
                                                    >
                                                        {{ subcategory.name }} (
                                                        {{ subcategory.grade_levels_display }}
                                                        )
                                                    </th>
                                                    <th
                                                        class="max-w-xs px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                                    >
                                                        <div class="flex space-x-1 justify-end">
                                                            <QualitativeReportSubcategoryForm
                                                                is-edit
                                                                :subcategory="subcategory"
                                                                :options="options"
                                                                :endpoint="subcategory.subcategory_update_endpoint"
                                                            />
                                                            <InertiaSquareConfirmActionButton
                                                                :confirm-text="$t('messages.delete')"
                                                                :href="subcategory.links.delete"
                                                                danger
                                                                method="delete"
                                                            >
                                                                <template v-slot:icon>
                                                                    <svg
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                        class="trash w-4 h-4"
                                                                    >
                                                                        <path
                                                                            fill-rule="evenodd"
                                                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                            clip-rule="evenodd"
                                                                        ></path>
                                                                    </svg>
                                                                </template>
                                                            </InertiaSquareConfirmActionButton>
                                                        </div>
                                                    </th>
                                                </tr>
                                                <tr
                                                    class="bg-white hover:bg-gray-100"
                                                    v-for="item in subcategory.items"
                                                    :key="`${item.id}_${item.updated_at}`"
                                                >
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                                    >
                                                        {{ item.name }}
                                                    </td>
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                                    >
                                                        <div class="flex space-x-1 justify-end">
                                                            <QualitativeReportItemForm
                                                                is-edit
                                                                :item="item"
                                                                :endpoint="item.item_update_endpoint"
                                                            />
                                                            <InertiaSquareConfirmActionButton
                                                                :confirm-text="$t('messages.delete')"
                                                                :href="item.links.delete"
                                                                danger
                                                                method="delete"
                                                            >
                                                                <template v-slot:icon>
                                                                    <svg
                                                                        viewBox="0 0 20 20"
                                                                        fill="currentColor"
                                                                        class="trash w-4 h-4"
                                                                    >
                                                                        <path
                                                                            fill-rule="evenodd"
                                                                            d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
                                                                            clip-rule="evenodd"
                                                                        ></path>
                                                                    </svg>
                                                                </template>
                                                            </InertiaSquareConfirmActionButton>
                                                        </div>
                                                    </td>
                                                </tr>
                                                <tr :class="'bg-white'">
                                                    <td
                                                        class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500"
                                                        colspan="2"
                                                    >
                                                        <QualitativeReportItemForm
                                                            :endpoint="subcategory.item_endpoint"
                                                        />
                                                    </td>
                                                </tr>
                                            </thead>
                                        </template>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="px-4 py-5 sm:p-6">
                        <QualitativeReportSubcategoryForm
                            :options="options"
                            :endpoint="qualitativeReportCategory.subcategory_endpoint"
                        />
                    </div>
                </div>
            </div>
        </div>

        <template v-slot:pagination>
            <InertiaPagination :paginator="qualitativeReportCategories" />
        </template>
    </PanelPage>
</template>

<script>
import AdminLayout from '@/Shared/AdminLayout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import InertiaPagination from '@/Shared/InertiaPagination.vue';
import InertiaSquareLinkButton from '@/components/InertiaSquareLinkButton.vue';
import AppButton from '@/components/AppButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import QualitativeReportSubcategoryForm from '@/components/pageSpecific/QualitativeReportSubcategoryForm.vue';
import QualitativeReportItemForm from '@/components/pageSpecific/QualitativeReportItemForm.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import AppButtonOptionsPanel from '@/components/AppButtonOptionsPanel.vue';

export default {
    components: {
        AppButtonOptionsPanel,
        PanelPage,
        InertiaPagination,
        InertiaSquareLinkButton,
        AppButton,
        EmptyState,
        QualitativeReportSubcategoryForm,
        QualitativeReportItemForm,
        InertiaSquareConfirmActionButton,
    },
    layout: AdminLayout,
    name: 'Index',
    props: ['meta', 'qualitativeReportCategories', 'filters', 'options'],
    data() {
        return {
            form: this.$inertia.form({
                search: this.filters.search,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.index);
        },
    },
};
</script>
